<template>
<div v-if="ready"> <!-- dummy root element -->
  <a name="shareholder-engagement-methodology"/>
  <div class="page">
    <p class="preheader">
      Corporates
    </p>
    <p class="dustin-title ">
      Shareholder Engagement
    </p>
    <div class="columns">
      <div class="column is-half">
        <p class="preheader">
          Impact Philosophy
        </p>
        <div class="description">
          <p>
            By communicating with more companies about environmental and social issues, investors may be able to convince more managers to implement changes. Academic literature has demonstrated a 18-60% likelihood that a shareholder engagement will be implemented by the target corporation, providing strong evidence that shareholder engagement is an effective mechanism through which investors can change company activities.[1]
          </p>
          <p>
          The amount of resources devoted to an engagement, and the persistence of the engagement, leads to a higher chance of success. Engagement as part of investor coalitions also increases the chance of success.
          </p>
        </div>
      </div>
      <div class="column is-half">
        <p class="preheader">
          Methodology
        </p>
        <div class="description">
          <p>
            The reported tallies of <span class="has-text-weight-semibold">"Companies Changed" </span> are instances where YourStake.org analysts have determined a tangible company commitment to change, in response to an active shareholder engagement campaign. This analyst verification step is intended to filter inflated reporting by fund managers, to assess impact outcomes as opposed to inputs, such as meetings held or letters sent by fund managers. YourStake.org examines companies changed on shareholder engagement by analyzing news, press releases, trade associations, and reports from fund managers directly. If an institution engages with a company on behalf of all of its funds, then all funds get engagement points.
          </p>
        </div>
      </div>
    </div>

    <p class="preheader">
      Coverage
    </p>
    <p class="description">
      Coverage is limited to the fund managers which explicitly engage in shareholder advocacy. The majority of fund manager engagement reporting is published in the fall, covering the previous year of activity (6-9 month lag).
    </p>

  <br>
  <br>
  <p class="dustin-label">
    Academic Citations
  </p>
  <p class='criteria'>
    <ol>
      <li>Kölbel, Julian and Heeb, Florian and Paetzold, Falko and Busch, Timo, Can Sustainable Investing Save the World? Reviewing the Mechanisms of Investor Impact (July 20, 2019). Kölbel, Julian F., Florian Heeb, Falko Paetzold, and Timo Busch. in press. ‘Can Sustainable Investing Save the World? Reviewing the Mechanisms of Investor Impact’. Organization & Environment. Available at: https://doi.org/10.1177/1086026620919202 , Available at SSRN: https://ssrn.com/abstract=3289544 or http://dx.doi.org/10.2139/ssrn.3289544</li>
    </ol>
  </p>

  </div><!-- page -->

  <div class="page">
    <a name="shareholder-engagement"/>
    <p class="preheader">
      Corporates
    </p>
    <p class="dustin-title">
      Shareholder Engagement
    </p>
    <br>
    <br>
    <div class="columns is-vcentered">
      <div class="column is-2 has-text-centered">
        <p class="title is-6">
          Impact Area
        </p>
      </div>
      <div class="column is-3 has-text-centered">
        <p class="title is-6">
          Companies Changed
        </p>
    </div>
      <div class="column has-text-centered">
        <p class="title is-6">
          Example Impact Stories
        </p>
      </div>
    </div>
    <br>

    <div class="issues">
      <div
        v-for="(engagementRow, index) in engagementReport"
        v-if="engagementRow.stories.length > 0"
        v-bind:key="engagementRow.esgissue_slug"
        class="columns jonbox is-vcentered print-no-break"
      >
        <div class="column is-one-sixth has-text-centered">
          <span v-bind:class="`icon is-large has-text-${engagementRow.esgissue_slug}`">
            <i v-bind:class="`fa-3x ${engagementRow.stories[0].subcategory[0].esgissue.icon}`"></i>
          </span>
          <p class="dustin-label has-text-centered">
            {{ engagementRow.stories[0].subcategory[0].esgissue.name }}
          </p>
        </div>
        <div class="column is-one-sixth has-text-centered">
          <p v-bind:class="`highlights has-text-${engagementRow.esgissue_slug}`">
            {{ engagementRow.engagements }}
          </p>
        </div>

        <!--
          This code is copy-pasted with slices of the stories array make two columns, because the original
          implementation with css column wrapping was inexplicably leaving large chunks of whitespace depending on
          the ordering of the stories themselves.
        -->
        <div class="column is-one-third">
          <div
            v-for="(story, index) in engagementRow.stories.slice(0, 2)"
            class="item"
          >
              <div class="columns">
                <div class="column is-narrow">
                  <figure class="image is-32x32">
                    <img v-bind:src="story.image">
                  </figure>
                </div>
                <div class="column">
                  <p class="criteria has-text-weight-semibold">
                    {{ story.title }}
                    &nbsp;
                    &nbsp;
                    <a
                      class="links has-text-grey has-text-weight-normal"
                      target='_blank'
                      v-bind:href="story.source_link">
                      Report
                    </a>
                  </p>
                </div>
              </div>

          </div>
        </div>

        <div class="column is-one-third">
          <div
            v-for="(story, index) in engagementRow.stories.slice(2, 4)"
            class="item"
          >
              <div class="columns">
                <div class="column is-narrow">
                  <figure class="image is-32x32">
                    <img v-bind:src="story.image">
                  </figure>
                </div>
                <div class="column">
                  <p class="criteria has-text-weight-semibold">
                    {{ story.title }}
                    &nbsp;
                    &nbsp;
                    <a
                      class="links has-text-grey has-text-weight-normal"
                      target='_blank'
                      v-bind:href="story.source_link">
                      Report
                    </a>
                  </p>
                </div>
              </div>

          </div>
        </div>

      </div>
    </div>
  </div>
</div>



</template>


<script>
  import eventBus from '@frontend/shared/EventBus.js';
  import { apiFetch } from '../../../api/api';

  export default {
    data () {
      return {
        ready: false,
        engagementReport: []
      }
    },
    name: 'ShareholderEngagement',
    props: {
      portfolio: Object,
    },
    computed: {},
    created: function() {
      var self = this;
      apiFetch(`/api/v1/portfolios/${this.portfolio.id}/report/engagement/`)
        .then((data) => {return data.json()})
        .then((data) => {
          self.engagementReport.push(...data.report);
          if (self.engagementReport.length > 0) {
            // If no shareholder engagement, we just don't render this section.
            for (let i in self.engagementReport) {
              if (self.engagementReport[i].stories.length > 0) {
                // If any issue has stories, then we can render the whole section, but if none of them do, despite having
                // companies changed metrics, don't bother rendering it.
                self.ready = true;
                eventBus.$emit('InstitutionalReport:page-rendered', this.$options.name);
              }
            }
          }
        });
    },
  }

</script>

<style lang="css" scoped>
  .persona_vegan .icon {
    color: #5aa9e6;
  }
  .persona_environmentalist .icon {
    color: #93C48B;
  }
  .persona_racial_justice .icon {
    color: #685369;
  }
  .persona_feminist .icon {
    color: #f1ab86;
  }
  .persona_virtuous .icon {
    color: #78CAD2;
  }
  .persona_labor .icon {
    color: #A40E4C;
  }
  .persona_law_and_order .icon {
    color: #8499B1;
  }

  .additional-notes {
    width: 100%;
    margin: 0 auto;
    text-align: center;
  }

  .additional-notes label, #notes-input {
    display: block;
    width: 75%;
    margin: 0 auto;
  }

  #notes-input {
    background-color: #fff;
    padding: 5px;
    height: 150px;
    margin: 15px auto;
    border: 2px solid lightgray;
  }

  .actions {
    text-align: center;
    margin: 0 auto;
  }

  .actions > a {
    display: block;
    margin-top: 20px;
  }

.moreshow {
  /* display: none */
}
p {
  margin-bottom: .75rem;
}
p:last-child {
  margin-bottom: 0rem;
}
.jonbox {
    border-radius: 2px;
    border: solid 2px var(--black-95);
    padding-top: .5rem;
    padding-bottom: .5rem;
}
.jonbox:not(:first-of-type) {
  margin-top: 1.5rem;
}

  .csscolumns {
    column-count: 2;
    column-rule-style: none;
    column-rule-width: 1px;
    column-rule-color: var(--black-95);
    column-fill: auto;
    -webkit-column-break-inside: avoid;
    page-break-inside: avoid;
    break-inside: avoid;
  }
  .item { /* Masonry bricks or child elements */
    display: inline-block;
    margin: 0 0 .5em;
    width: 100%;
  }

</style>
